import type { EXPERIMENT_STATUSES } from 'definitions';
import type { OutcomeReading } from './classify-outcome';
import { resolveReportCurrency } from 'helpers/currency';

type ExperimentStatus = (typeof EXPERIMENT_STATUSES)[number];
export type ProgramStatus = Exclude<ExperimentStatus, 'Draft' | 'QA'>;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * The furthest back a request may reach. The rows come back unaggregated, so this is
 * what bounds the payload; three years covers any window the screen offers.
 */
export const MAX_LOOKBACK_MS = 3 * 365 * DAY_MS;

/** Used when the caller sends no usable `since`: the screen's own default. */
export const DEFAULT_LOOKBACK_MS = 365 * DAY_MS;

/**
 * One experiment, as the programme screen reads it.
 *
 * Unaggregated on purpose. Month buckets depend on the viewer's timezone, and the
 * breakdowns are cheap over a few hundred rows, so the client derives them rather
 * than the server guessing a zone and shipping a shape per chart.
 */
export type ProgramRow = {
  experimentId: number;
  name: string;
  websiteId: number;
  websiteUrl: string;
  status: ProgramStatus;
  createdAt: number | null;
  startedAt: number;
  /** When the randomised comparison stopped. Null while it is still running. */
  concludedAt: number | null;
  implementedAt: number | null;
  /** First time a variant was rolled out to everyone, whatever the status is now. */
  deployedAt: number | null;
  /** PXL importance, 0-10, or null when the experiment was never scored. */
  importance: number | null;
  /** Stored as-is; the dashboard owns the parser for its two historical shapes. */
  expectedMechanisms: unknown;
  /**
   * Sessions across the randomised arms. A deployed clone is excluded: its traffic is
   * the rollout, which the significance reading already leaves out too.
   */
  sessions: number;
  /** Null while running: an outcome is only a conclusion once the test has ended. */
  reading: OutcomeReading | null;
};

export type ProgramAnalyticsResponse = {
  isAgency: boolean;
  /** The window actually applied, after clamping. */
  since: number;
  rows: ProgramRow[];
};

/**
 * The caller's window start, clamped to what the server is willing to return.
 *
 * Invalid input falls back to the default rather than erroring: the value comes from
 * a date control, and a malformed one is a stale bookmark, not an attack worth a 400.
 */
export function resolveSince(raw: string | undefined, now: number): number {
  const since = Number(raw);
  if (!raw || !Number.isFinite(since)) return now - DEFAULT_LOOKBACK_MS;
  return Math.min(now, Math.max(now - MAX_LOOKBACK_MS, since));
}

type ConcludedAtParams = {
  status: ProgramStatus;
  endsAt: number | null;
  /** Earliest `deployed` stamp across the experiment's variants. */
  firstDeployedAt: number | null;
};

/**
 * When the comparison stopped, which is not always endsAt.
 *
 * The randomising window ends at the first deployment (see deployVariant), so that is
 * when a deployed test concluded. Deploying stamps no endsAt, and a deployed test can
 * still be ended by hand later, leaving it Ended with an endsAt that may be months
 * into the rollout. So for both statuses the conclusion is whichever came first;
 * endsAt alone would add the rollout to the test's length.
 */
export function concludedAt({ status, endsAt, firstDeployedAt }: ConcludedAtParams): number | null {
  if (status === 'Running') return null;

  const stamps = [endsAt, firstDeployedAt].filter((stamp): stamp is number => stamp !== null);
  return stamps.length ? Math.min(...stamps) : null;
}

type InWindowParams = { startedAt: number; concludedAt: number | null; status: ProgramStatus; since: number };

/**
 * Whether an experiment is part of the window at all.
 *
 * Started inside it counts toward velocity; concluded inside it counts toward
 * outcomes; still running counts as in flight whenever it started. A test that
 * started and concluded before the window has nothing to say about it.
 */
export function isInWindow({ startedAt, concludedAt, status, since }: InWindowParams): boolean {
  if (startedAt >= since) return true;
  if (status === 'Running') return true;
  return concludedAt !== null && concludedAt >= since;
}

type CurrencyKeys = { experimentId: number; currencies: readonly string[] | null };

/**
 * Which currency bucket to read each experiment under, grouped so each bucket is one
 * query.
 *
 * Resolved exactly as the statistics screen resolves its default (resolveReportCurrency):
 * the aggregate 'nill' bucket when it exists, otherwise the first currency present.
 * Hard-coding 'nill' read a site that always reports a currency (a SEK-only shop has
 * no 'nill' bucket at all) as having no data: 778 of 2,138 experiments on 2026-09-25.
 *
 * Keys are sorted first because Postgres returns jsonb keys in its own storage order,
 * while the screen's currency list is ORDER BY currency_code ASC. An experiment with
 * no buckets is left out, and so reads as no data.
 */
export function groupByReportCurrency(rows: readonly CurrencyKeys[]): Map<string, number[]> {
  const groups = new Map<string, number[]>();
  for (const { experimentId, currencies } of rows) {
    const currency = resolveReportCurrency([...(currencies ?? [])].sort());
    if (!currency) continue;
    groups.set(currency, [...(groups.get(currency) ?? []), experimentId]);
  }
  return groups;
}

/** Complete PXL scores or nothing: a partial set was never a rating. */
export function hasPxlScores(raw: unknown): boolean {
  if (!raw || typeof raw !== 'object') return false;
  const { p, x, l } = raw as Record<string, unknown>;
  return [p, x, l].every((score) => typeof score === 'number' && Number.isFinite(score) && score > 0);
}
